import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { prefersReducedMotion, preloadImage, debounce } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {


  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    if (this.refs.mediaGallery) {
      document.addEventListener('scroll', this.handleScroll);
    }

  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();

    if (this.refs.mediaGallery) {
      document.removeEventListener('scroll', this.handleScroll);
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  
    /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
    handleScroll = debounce(async () => {
      const { media, mediaThumbnails } = this.refs;

      const mostVisibleElement = await getMostVisibleElement(media);
      const activeIndex = media.indexOf(mostVisibleElement);
      const targetThumbnail = mediaThumbnails.children[activeIndex];
  
      if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;
  
      Array.from(mediaThumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === activeIndex}`);
      });
  
      this.#loadHighResolutionImage(mostVisibleElement);
    }, 50);

    /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
    #loadHighResolutionImage(mediaContainer) {
      if (!mediaContainer.classList.contains('product-media-container--image')) return false;
  
      const image = mediaContainer.querySelector('img.product-media__image');
      if (!image || !(image instanceof HTMLImageElement)) return false;
  
      const highResolutionUrl = image.getAttribute('data_max_resolution');
      if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;
  
      preloadImage(highResolutionUrl);
  
      const newImage = new Image();
      newImage.className = image.className;
      newImage.alt = image.alt;
      newImage.setAttribute('data_max_resolution', highResolutionUrl);
  
      // When the high-resolution image loads, replace the existing image
      newImage.onload = () => {
        image.replaceWith(newImage);
        this.#highResImagesLoaded.add(highResolutionUrl);
      };
  
      newImage.src = highResolutionUrl;
    }
  
  
    /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
    async selectThumbnail(index, options = { behavior: 'smooth' }) {
      if (!this.refs.mediaThumbnails || !this.refs.mediaThumbnails.children.length) return;
  
      // Guard if invalid
      if (isNaN(index) || index < 0 || index >= this.refs.mediaThumbnails.children.length) return;
  
      const { media, mediaThumbnails } = this.refs;
      const targetThumbnail = mediaThumbnails.children[index];
  
      if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;
  
      Array.from(mediaThumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === index}`);
      });
  
      scrollIntoView(targetThumbnail, {
        ancestor: mediaThumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });
  
      const targetImage = media[index];
  
      if (targetImage) {
        targetImage.scrollIntoView({
          behavior: options.behavior,
        });
  
        this.#loadHighResolutionImage(targetImage);
      }
      this.dispatchEvent(new ZoomMediaSelectedEvent(index));
    }

    /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
    async handleMediaThumbnailClick(index) {
      const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
      this.selectThumbnail(index, { behavior });
    }


  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}


/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}